import type { Role } from '@maktech/shared';
import type { AuthSession } from '@/api/auth.api';
import type { AuthUser } from '@/features/auth/AuthContext';

const SESSION_KEY = 'maktech_demo_auth_session';

/** Frontend-only demo passwords — never hits the backend when USE_DEMO is on. */
export const DEMO_PASSWORDS: Record<string, string> = {
  'admin@maktechacademy.com': 'admin123',
  'support@maktechacademy.com': 'support123',
  'instructor@maktechacademy.com': 'instructor123',
  'imran@example.com': 'instructor123',
  'orders@maktechacademy.com': 'orders123',
  'sadia@demo.com': 'demo123',
  'nusrat@demo.com': 'demo123',
  'rahim@demo.com': 'demo123',
  'karim@demo.com': 'demo123',
  'affiliate@demo.com': 'demo123',
};

/** One quick-fill account per role for the login page (demo only). */
export const DEMO_LOGIN_ACCOUNTS = [
  { role: 'Superadmin', email: 'admin@maktechacademy.com', password: 'admin123' },
  { role: 'Support', email: 'support@maktechacademy.com', password: 'support123' },
  { role: 'Instructor', email: 'instructor@maktechacademy.com', password: 'instructor123' },
  { role: 'Instructor (app)', email: 'imran@example.com', password: 'instructor123' },
  { role: 'Order manager', email: 'orders@maktechacademy.com', password: 'orders123' },
  { role: 'Affiliate', email: 'affiliate@demo.com', password: 'demo123' },
  { role: 'Student', email: 'sadia@demo.com', password: 'demo123' },
] as const;

const DEMO_USERS: AuthUser[] = [
  {
    id: 'u_admin',
    email: 'admin@maktechacademy.com',
    name: 'Admin User',
    role: 'superadmin',
    avatarInitials: 'AU',
    points: 0,
    streak: 0,
  },
  {
    id: 'u_support',
    email: 'support@maktechacademy.com',
    name: 'Support Agent',
    role: 'support',
    avatarInitials: 'SA',
    points: 0,
    streak: 0,
  },
  {
    id: 'u_instructor',
    email: 'instructor@maktechacademy.com',
    name: 'Tanvir Hasan',
    role: 'instructor',
    avatarInitials: 'TH',
    points: 0,
    streak: 0,
  },
  {
    id: 'u_imran_instr',
    email: 'imran@example.com',
    name: 'Imran Chowdhury',
    role: 'instructor',
    avatarInitials: 'IC',
    points: 0,
    streak: 0,
  },
  {
    id: 'u_ordermgr',
    email: 'orders@maktechacademy.com',
    name: 'Order Manager',
    role: 'order_manager',
    avatarInitials: 'OM',
    points: 0,
    streak: 0,
  },
  {
    id: 'u_sadia',
    email: 'sadia@demo.com',
    name: 'Sadia Rahman',
    role: 'student',
    avatarInitials: 'SR',
    points: 420,
    streak: 3,
  },
  {
    id: 'u_nusrat',
    email: 'nusrat@demo.com',
    name: 'Nusrat Jahan',
    role: 'student',
    avatarInitials: 'NJ',
    points: 210,
    streak: 1,
  },
  {
    id: 'u_rahim',
    email: 'rahim@demo.com',
    name: 'Rahim Khan',
    role: 'student',
    avatarInitials: 'RK',
    points: 890,
    streak: 5,
  },
  {
    id: 'u_affiliate',
    email: 'affiliate@demo.com',
    name: 'Nasir Ahmed',
    role: 'affiliate',
    avatarInitials: 'NA',
    points: 150,
    streak: 0,
  },
];

function delay(ms = 120): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

function saveSession(session: AuthSession): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* ignore quota / private mode */
  }
}

function readSession(): AuthSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

function clearSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

function authError(message: string): Error {
  const err = new Error(message) as Error & { response?: { data?: { error?: { message: string } } } };
  err.response = { data: { error: { message } } };
  return err;
}

export async function demoLogin(email: string, password: string): Promise<AuthSession> {
  await delay();
  const key = email.trim().toLowerCase();
  const expected = DEMO_PASSWORDS[key];
  if (!expected || password !== expected) {
    throw authError('Incorrect email or password');
  }
  const user = DEMO_USERS.find((u) => u.email.toLowerCase() === key);
  if (!user) throw authError('Incorrect email or password');
  const session: AuthSession = {
    user: { ...user },
    accessToken: `demo-token-${user.id}`,
  };
  saveSession(session);
  return session;
}

export async function demoSignup(name: string, email: string, password: string): Promise<AuthSession> {
  await delay();
  const key = email.trim().toLowerCase();
  if (DEMO_PASSWORDS[key] || DEMO_USERS.some((u) => u.email.toLowerCase() === key)) {
    throw authError('Email already in use');
  }
  if (password.length < 8 || !/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
    throw authError('Password must be at least 8 characters and include a letter and a number');
  }
  const user: AuthUser = {
    id: `u_signup_${Date.now()}`,
    email: key,
    name: name.trim() || 'Learner',
    role: 'student' as Role,
    avatarInitials: initials(name.trim() || 'LR'),
    points: 0,
    streak: 0,
  };
  DEMO_USERS.push(user);
  DEMO_PASSWORDS[key] = password;
  const session: AuthSession = { user, accessToken: `demo-token-${user.id}` };
  saveSession(session);
  return session;
}

export async function demoRefreshSession(): Promise<AuthSession> {
  await delay(40);
  const session = readSession();
  if (!session?.user || !session.accessToken) {
    throw authError('Not authenticated');
  }
  return session;
}

export async function demoLogout(): Promise<void> {
  await delay(40);
  clearSession();
}
